'use client';

import { useEffect, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { AnalyticsProvider } from '@/contexts/AnalyticsContext';

// posthog-js (~200KB) is already deferred: lib/analytics/posthog-client only
// `import('posthog-js')` on the first analytics call, so AnalyticsProvider and
// its module graph are cheap to import here and safe to render on the server.
//
// AnalyticsProvider therefore wraps `children` from the very first render and
// never changes position in the tree. The previous version swapped the root
// element from `<>{children}</>` to `<AnalyticsProvider>{children}</…>` once an
// idle callback fired — and because the element TYPE changed (Fragment →
// AnalyticsProvider), React unmounted and remounted the entire app, blanking
// the page for a moment on first load (the reported "flash"). Keeping the
// wrapper constant removes that remount (and the window where children had no
// provider above them, which made useAnalytics() throw).
const ConsentBanner = dynamic(
  () => import('./ConsentBanner').then((m) => m.ConsentBanner),
  { ssr: false }
);

export function LazyAnalytics({ children }: { children: ReactNode }) {
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    // Defer only the consent banner (a sibling) until after first paint.
    // Mounting a sibling after `children` does not remount `children`.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setShowConsent(true));
    } else {
      setTimeout(() => setShowConsent(true), 1);
    }
  }, []);

  return (
    <AnalyticsProvider>
      {children}
      {showConsent && <ConsentBanner />}
    </AnalyticsProvider>
  );
}
